import Container, { Service } from 'typedi';
import {
    IAuthenticationResult,
    IAuthenticationService,
    IAuthenticationStorage,
    IAutologinAuthenticationService,
    IAutoLoginResponseModel,
    IExecuteWithAnalyticsResult,
    ILoginOptions,
    IRegistrationAutoLoginModel,
} from '../models';

import { AuthenticationFlowType } from '../enums';
import { StorageItemEnum } from '../../../models/enums/storage-enums';
import { ActionID, ISessionChannel, MessageBroker } from '@sparkware/uc-sdk-core';
import { RegistrationAutoLoginDataBuilder } from './registration-autologindata.builder.service';

import {
    LocalSimpleStoreService,
    SessionSimpleStoreService,
} from '../../storage/implementations/simple-store';
import merge from 'lodash/merge';
import { AuthenticationService } from './authentication.service';
import { AutoLoginAuthenticationService } from './autologin-authentication.service';
import { LoaderManager } from '../../../loaders/LoaderManager';

@Service()
export class RegistrationAuthenticationService {
    private readonly _autoLoginAuthenticationService?: IAutologinAuthenticationService;
    private readonly _authenticationService?: IAuthenticationService;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _sessionSimpleStoreService: SessionSimpleStoreService;
    private readonly _registrationAutoLoginDataBuilder: RegistrationAutoLoginDataBuilder;
    private readonly _sessionChannel: ISessionChannel;

    private get _authenticationStoragePromise(): Promise<IAuthenticationStorage> {
        return LoaderManager.Instance.AuthenticationStorageLoader.Instance;
    }

    constructor() {
        this._sessionSimpleStoreService = Container.get(SessionSimpleStoreService);
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this._registrationAutoLoginDataBuilder = Container.get(RegistrationAutoLoginDataBuilder);
        this._sessionChannel = MessageBroker.getInstance().session;
        this._authenticationService = Container.get(AuthenticationService);
        this._autoLoginAuthenticationService = Container.get(AutoLoginAuthenticationService);
    }

    public autoLogin = async (
        data: any,
        options?: ILoginOptions,
    ): Promise<IAuthenticationResult<IAutoLoginResponseModel>> => {
        const authenticationStorage = await this._authenticationStoragePromise;
        authenticationStorage.setLoginOptions(this._enhanceLoginOptions(options));

        const loginResult: IExecuteWithAnalyticsResult<IAutoLoginResponseModel> =
            await this._authenticationService.executeWithAnalytics(
                AuthenticationFlowType.Registration,
                async (): Promise<IAuthenticationResult<IAutoLoginResponseModel>> => {
                    const autoLoginData: IRegistrationAutoLoginModel =
                        await this._createAutoLoginData(data);

                    const result =
                        await this._autoLoginAuthenticationService.autoLogin(autoLoginData);
                    const { response, errorResponse } = result || {};
                    if (errorResponse || !response?.Response?.BearerToken) {
                        this._sessionSimpleStoreService.remove(
                            StorageItemEnum.CashierOpenStartTimestamp,
                        );
                        if (
                            this._localSimpleStoreService.get(StorageItemEnum.ActionStarted) ===
                            ActionID.register
                        ) {
                            this._sessionChannel.topics.actionCompleted.publish(
                                { publisher: 'AuthenticationService' },
                                {},
                            );
                        }
                    }

                    return result;
                },
            );
        await this._authenticationService.startOfferingAuthentication(loginResult);
        return loginResult?.result;
    };

    private _createAutoLoginData = async (data): Promise<IRegistrationAutoLoginModel> => {
        if (!data) return null;

        return await this._registrationAutoLoginDataBuilder.Build(data);
    };

    private _enhanceLoginOptions = (options: ILoginOptions = {}): ILoginOptions => {
        const defaultRegistrationOptions: ILoginOptions = {
            postLogin: {
                ActivityHistory: { disabled: true },
                StartFavouriteSportsFlow: { isRegistration: true },
                ShowPlayerWinLoss: { isRegistration: true },
            },
        };

        return merge(defaultRegistrationOptions, options);
    };
}
